import gtJsBridge from '@gaotu/js-bridge';
// import gtJsBridgeOld from '@gt/js-bridge';
import {bridgeCallLogger} from '@/utils/logger';
import useUa, {XApp} from '@/hooks/useUa';
import {useDeviceInfo} from '@/hooks/useDeviceInfo';
import {isHigherVersion} from '@/utils/version';
import {haboPerf} from '@/plugins/haboConfig';
import {EventIdType, getEventId} from '@/enum/haboEventId';
import nativeData from './nativeData';

const {callNative, asyncCallNative} = gtJsBridge;
const {isApp, isXApp, isIOS, isAndroid} = useUa();


// 状态栏透明
export const hideNavigationStatusBar = () => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('handleNavigationStatusBar');
  callNative({
    apiName: 'handleNavigationStatusBar',
    params: {type: 2}
  });
};

// 返回上一级页面, 没有上一级就退出当前窗口
export const closeWindow = () => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('closeWindow');
  callNative({
    apiName: 'closeWindow',
  });
};

// 查看 pdf excel 等
export const openMaterial = (name: string, url: string) => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('openMaterial', {name, url});
  callNative({
    apiName: 'openMaterial',
    params: {name, url}
  });
};

// 跳转到登录页
export const toLoginPage = async (callback?: (info: any) => void) => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('userLogin');
  if (isAndroid) {
    // eslint-disable-next-line no-empty-function, @typescript-eslint/no-empty-function
    gtJsBridge.registerCallback('nativeLoginCallback', callback || (() => {}));
  }
  // useCallback - 登录后通知原页面还是跳转页面: "0"-跳转 "1"-通知页面
  // redirectUrl - 登录后要跳转的页面
  const res = await asyncCallNative({
    apiName: 'userLogin',
    params: {
      useCallback: '1',
      redirectUrl: window.location.href// 项目部署的地址
    }
  });
  callback?.(res.data);
};

// 分享到微信好友或朋友圈
export const shareUrlToWechat = (params: {
  type: 0 | 1;// 0-好友 1-朋友圈
  title: string;
  content: string;
  img: string;
  url: string;
}) => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('shareUrlToWechat', params);
  callNative({
    apiName: 'shareUrlToWechat',
    params
  });
};

// 复制链接到剪切板
export const copyStr = (content: string) => {
  bridgeCallLogger('copyStr', {content});
  callNative({
    apiName: 'copyStr',
    params: {content}
  });
};

// 打开外链
export const openWindow = (url: string, showNavigationBar: 0 | 1 = 1, showStatusBar: 0 | 1 = 1) => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('openWindow', {url, isNoah: 1, showNavigationBar, showStatusBar});
  callNative({
    apiName: 'openWindow',
    params: {url, isNoah: 1, showNavigationBar, showStatusBar}
  });
};

enum AppSchemeEnum {
  isGaotuApp = 'superclass',
  isGongkaoApp = 'supergongkao',
  isTutuApp = 'supertutu',
  isJingpinApp = 'superjingpin',
  isXinliApp = 'superxinli',
  isKetangApp = 'superketang'
}


// 跳转到反馈页面
export const showFeedback = () => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('showFeedback');
  const protocol = AppSchemeEnum[(Object.keys(isXApp) as (keyof XApp)[]).find((item: keyof XApp) => isXApp[item])!];
  window.open(`${protocol}://jump?category=native&action_type=feed_back`);
};

// 提示更新 App
export const showUpgradeApp = () => {
  if (!isApp) {
    return;
  }
  bridgeCallLogger('showUpgradeApp');
  callNative({
    apiName: 'showUpgradeApp',
    params: {}
  });
};

// 保存图片到本地, 只在客户端内调用, 浏览器或其他场景一般默认有处理
export const saveImageToAlbum = (imageURL: string) => {
  if (!isApp) {
    return;
  }
  // 高途 app 内保存图片版本判断, 公考 app 第一版就支持
  const {appVersion} = useDeviceInfo();
  if (isXApp.isGaotuApp && !isHigherVersion(appVersion, '4.54.0')) {
    showUpgradeApp();
    return;
  }
  bridgeCallLogger('saveImageToAlbum');

  callNative({
    apiName: 'saveImageToAlbum',
    params: {imageURL}
  });
};

// 禁用启用 ios 手势, 0-禁用, 1-启用
export const enableIOSGesture = (webEnable: 0 | 1, pageEnable: 0|1) => {
  if (!isIOS || !isApp) {
    return;
  }
  bridgeCallLogger('enableIOSBackForwardNavigationGestures', {webEnable, pageEnable});

  callNative({
    apiName: 'enableIOSBackForwardNavigationGestures',
    params: {webEnable, pageEnable}
  });
};

// 上报fmp, fcp由html里的js上报
let alreadyReportFmp = false;
export const reportLog = (typeName: 'fmp') => {
  if (typeName === 'fmp' && !alreadyReportFmp) {
    alreadyReportFmp = true;
    haboPerf('fmp');
  }
  if (isApp) {
    const typeMap = {
      fcp: 'speed',
      fmp: 'fmp_speed'
    };
    bridgeCallLogger('reportLog', {type: typeMap[typeName]});
    callNative({
      apiName: 'reportLog',
      params: {type: typeMap[typeName]}
    });
  }
};

export const getNativeData = async (url: string) => {
  if (!isApp) {
    return null;
  }
  bridgeCallLogger('requestData', {
    dataApi: url
  });
  try {
    const nativeRes = await gtJsBridge.asyncCallNative<
      {
        pageUrl: string; dataApi: string;
      },
      {
        timestamp: string;
        response: string;
      }
    >({
      apiName: 'requestData',
      params: {
        // @ts-ignore
        pageUrl: location.href,
        dataApi: url
      },
    });
    const res = nativeRes.data;
    console.log('url: ', url, 'res: ', res);
    if (res?.response) {
      return JSON.parse(res.response);
    }
    return null;
  }
  catch (err) {
    return null;
  }
  // const data: any = await nativeData(url, 50);
};

let hasNotify = false;
export const notifyPage = () => {
  if (hasNotify) {
    return;
  }
  if (!isApp) {
    return;
  }
  bridgeCallLogger('notifyPage');
  hasNotify = true;
  callNative({
    apiName: 'notifyPage',
    params: {
      updateCache: {
        updateCurPage: '1'
      }
    }
  });
};

export const setWebBackScheme = (scheme: string) => {
  if (!isApp) {
    return;
  }
  const params = {scheme};
  bridgeCallLogger('setWebBackScheme');
  callNative({
    apiName: 'setWebBackScheme',
    params
  });
};

// other不能为空, 不然在旧版本中不能使用
export const timeReportIOS = (eventName: keyof EventIdType, other?: IObject) => {
  if (!isApp || !isIOS) {
    return;
  }
  bridgeCallLogger('notifyPage', {
    webPageView: {
      event_id: `${getEventId(eventName)}`,
      timeKey: 'stay_time',
      timestamp: Date.now(),
      haboData: other || {placeholder: ''}
    }
  });
  callNative({
    apiName: 'notifyPage',
    params: {
      webPageView: {
        event_id: `${getEventId(eventName)}`,
        timeKey: 'stay_time',
        timestamp: Date.now(),
        haboData: other || {placeholder: ''}
      },
    }
  });
};

